/**
 * Asset Form Component
 * Multi-section form for creating and editing assets
 */

import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { CurrencyInput } from '../ui/currency-input';
import { RichTextEditor } from '../ui/rich-text-editor';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useCategoryStore, useSubcategoryStore } from '../../stores/categories';
import { useNextIdentifier } from '../../stores/asset-list';
import type { CreateAssetFormData, UpdateAssetFormData } from '../../lib/validation/asset-schema';
import type { LifecycleStatus, DispositionStatus, StreetAddress, DeliveryMethod } from '../../types/api';
import { LiveField } from './LiveField';
import type { FieldHandle } from '../../lib/live-form-store';

const lifecycleStatuses: LifecycleStatus[] = [
  'Inventoried',
  'Valued',
  'Listed for Sale',
  'Under Offer',
  'Sold',
  'Payment Pending',
  'Delivered',
  'Disposed',
  'Archived',
];

const dispositionStatuses: DispositionStatus[] = [
  'Sell',
  'Keep',
  'Give Away',
  'Donate',
  'Other',
];

const streetAddresses: StreetAddress[] = [
  '30 Light St',
  '28 Light St',
  '15 Templar',
  '30 Workshop',
  'Other',
];

const deliveryMethods: DeliveryMethod[] = [
  'Pickup',
  'Courier',
  'Australia Post',
  'Freight',
  'Other',
];

interface AssetFormProps {
  form: UseFormReturn<CreateAssetFormData | UpdateAssetFormData>;
  onSubmit: (data: CreateAssetFormData | UpdateAssetFormData) => void;
  mode: 'create' | 'edit';
  isPending: boolean;
  liveField?: <T = string>(fieldName: string) => FieldHandle<T>;
}

export function AssetForm({ form, onSubmit, mode, isPending, liveField }: AssetFormProps) {
  const selectedCategory = form.watch('category_id');
  const streetAddress = form.watch('street_address');
  const dispositionStatus = form.watch('disposition_status');
  const deliveryMethod = form.watch('delivery_method');
  const lifecycleStatus = form.watch('lifecycle_status');

  useCategoryStore.live();
  const categories = useCategoryStore(s => s.categories);

  const setCategoryId = useSubcategoryStore(s => s.setCategoryId);
  useEffect(() => { setCategoryId(selectedCategory || undefined); }, [selectedCategory, setCategoryId]);
  useSubcategoryStore.live();
  const subcategories = useSubcategoryStore(s => s.subcategories);
  const { mutate: fetchNextId } = useNextIdentifier();

  useEffect(() => {
    if (mode === 'create' && !form.getValues('unique_identifier')) {
      fetchNextId().then((data: any) => {
        form.setValue('unique_identifier', data.next_id);
      }).catch(() => {});
    }
  }, [mode, form, fetchNextId]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Basic Information</h3>

          {mode === 'create' && (
            <FormField
              control={form.control}
              name="unique_identifier"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unique Identifier *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                    />
                  </FormControl>
                  <FormDescription>
                    Auto-generated. You can override if needed.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="asset_nickname"
            render={({ field: rhfField }) => {
              const live = liveField?.("asset_nickname");
              const value = live?.value ?? rhfField.value ?? "";
              return (
                <FormItem>
                  <FormLabel>Asset Nickname *</FormLabel>
                  <LiveField lockedBy={live?.lockedBy ?? null}>
                    <FormControl>
                      <Input
                        value={value}
                        onChange={(e) => { rhfField.onChange(e.target.value); live?.onChange(e.target.value); }}
                        onFocus={live?.onFocus}
                        onBlur={() => { rhfField.onBlur(); live?.onBlur(); }}
                        disabled={live ? !live.isEditable : false}
                      />
                    </FormControl>
                  </LiveField>
                  <FormDescription>
                    A short, memorable name for this asset
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="category_id"
            render={({ field: rhfField }) => {
              const live = liveField?.<number | null>("category_id");
              const value = live?.value ?? rhfField.value ?? null;
              return (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <LiveField lockedBy={live?.lockedBy ?? null}>
                    <Select
                      value={String(value ?? "none")}
                      onValueChange={(v) => {
                        const val = v === "none" ? null : parseInt(v, 10);
                        rhfField.onChange(val);
                        live?.onChange(val);
                        form.setValue('subcategory_id', null);
                      }}
                      disabled={live ? !live.isEditable : false}
                    >
                      <FormControl>
                        <SelectTrigger onFocus={live?.onFocus} onBlur={live?.onBlur}>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {categories.map((cat) => (
                          <SelectItem key={cat.category_id} value={cat.category_id.toString()}>
                            {cat.category_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </LiveField>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {selectedCategory && (
            <FormField
              control={form.control}
              name="subcategory_id"
              render={({ field: rhfField }) => {
                const live = liveField?.<number | null>("subcategory_id");
                const value = live?.value ?? rhfField.value ?? null;
                return (
                  <FormItem>
                    <FormLabel>Subcategory</FormLabel>
                    <LiveField lockedBy={live?.lockedBy ?? null}>
                      <Select
                        value={String(value ?? "none")}
                        onValueChange={(v) => {
                          const val = v === "none" ? null : parseInt(v, 10);
                          rhfField.onChange(val);
                          live?.onChange(val);
                        }}
                        disabled={live ? !live.isEditable : false}
                      >
                        <FormControl>
                          <SelectTrigger onFocus={live?.onFocus} onBlur={live?.onBlur}>
                            <SelectValue placeholder="Select subcategory" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {subcategories.map((sub) => (
                            <SelectItem key={sub.subcategory_id} value={sub.subcategory_id.toString()}>
                              {sub.subcategory_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </LiveField>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          )}
        </div>

        {/* Location */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Location</h3>

          <FormField
            control={form.control}
            name="street_address"
            render={({ field: rhfField }) => {
              const live = liveField?.("street_address");
              const value = live?.value ?? rhfField.value ?? "none";
              return (
                <FormItem>
                  <FormLabel>Street Address</FormLabel>
                  <LiveField lockedBy={live?.lockedBy ?? null}>
                    <Select
                      value={String(value || "none")}
                      onValueChange={(v) => {
                        const val = v === "none" ? null : v;
                        rhfField.onChange(val);
                        live?.onChange(val as any);
                      }}
                      disabled={live ? !live.isEditable : false}
                    >
                      <FormControl>
                        <SelectTrigger onFocus={live?.onFocus} onBlur={live?.onBlur}>
                          <SelectValue placeholder="Select address" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {streetAddresses.map((address) => (
                          <SelectItem key={address} value={address}>
                            {address}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </LiveField>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {streetAddress === 'Other' && (
            <FormField
              control={form.control}
              name="street_address_other"
              render={({ field: rhfField }) => {
                const live = liveField?.("street_address_other");
                const value = live?.value ?? rhfField.value ?? "";
                return (
                  <FormItem>
                    <FormLabel>Other Address</FormLabel>
                    <LiveField lockedBy={live?.lockedBy ?? null}>
                      <FormControl>
                        <Input
                          value={value}
                          onChange={(e) => { rhfField.onChange(e.target.value); live?.onChange(e.target.value); }}
                          onFocus={live?.onFocus}
                          onBlur={() => { rhfField.onBlur(); live?.onBlur(); }}
                          disabled={live ? !live.isEditable : false}
                        />
                      </FormControl>
                    </LiveField>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          )}

          <FormField
            control={form.control}
            name="specific_location"
            render={({ field: rhfField }) => {
              const live = liveField?.("specific_location");
              const value = live?.value ?? rhfField.value ?? "";
              return (
                <FormItem>
                  <FormLabel>Specific Location</FormLabel>
                  <LiveField lockedBy={live?.lockedBy ?? null}>
                    <FormControl>
                      <Textarea
                        value={value}
                        onChange={(e) => { rhfField.onChange(e.target.value); live?.onChange(e.target.value); }}
                        onFocus={live?.onFocus}
                        onBlur={() => { rhfField.onBlur(); live?.onBlur(); }}
                        disabled={live ? !live.isEditable : false}
                        rows={2}
                      />
                    </FormControl>
                  </LiveField>
                  <FormDescription>
                    e.g., "Room 3, Top Shelf" or "Warehouse A, Bay 12"
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>

        {/* Lifecycle & Disposition */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Lifecycle & Disposition</h3>

          <FormField
            control={form.control}
            name="lifecycle_status"
            render={({ field: rhfField }) => {
              const live = liveField?.("lifecycle_status");
              const value = live?.value ?? rhfField.value ?? "Inventoried";
              return (
                <FormItem>
                  <FormLabel>Lifecycle Status</FormLabel>
                  <LiveField lockedBy={live?.lockedBy ?? null}>
                    <Select
                      value={String(value || "Inventoried")}
                      onValueChange={(v) => {
                        rhfField.onChange(v);
                        live?.onChange(v as any);
                      }}
                      disabled={live ? !live.isEditable : false}
                    >
                      <FormControl>
                        <SelectTrigger onFocus={live?.onFocus} onBlur={live?.onBlur}>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {lifecycleStatuses.map((status) => (
                          <SelectItem key={status} value={status}>
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </LiveField>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="disposition_status"
            render={({ field: rhfField }) => {
              const live = liveField?.("disposition_status");
              const value = live?.value ?? rhfField.value ?? "none";
              return (
                <FormItem>
                  <FormLabel>Disposition</FormLabel>
                  <LiveField lockedBy={live?.lockedBy ?? null}>
                    <Select
                      value={String(value || "none")}
                      onValueChange={(v) => {
                        const val = v === "none" ? null : v;
                        rhfField.onChange(val);
                        live?.onChange(val as any);
                      }}
                      disabled={live ? !live.isEditable : false}
                    >
                      <FormControl>
                        <SelectTrigger onFocus={live?.onFocus} onBlur={live?.onBlur}>
                          <SelectValue placeholder="Select disposition" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {dispositionStatuses.map((status) => (
                          <SelectItem key={status} value={status}>
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </LiveField>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {dispositionStatus === 'Other' && (
            <FormField
              control={form.control}
              name="disposition_other"
              render={({ field: rhfField }) => {
                const live = liveField?.("disposition_other");
                const value = live?.value ?? rhfField.value ?? "";
                return (
                  <FormItem>
                    <FormLabel>Other Disposition</FormLabel>
                    <LiveField lockedBy={live?.lockedBy ?? null}>
                      <FormControl>
                        <Input
                          value={value}
                          onChange={(e) => { rhfField.onChange(e.target.value); live?.onChange(e.target.value); }}
                          onFocus={live?.onFocus}
                          onBlur={() => { rhfField.onBlur(); live?.onBlur(); }}
                          disabled={live ? !live.isEditable : false}
                        />
                      </FormControl>
                    </LiveField>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          )}

          <FormField
            control={form.control}
            name="disposal_avenue"
            render={({ field: rhfField }) => {
              const live = liveField?.("disposal_avenue");
              const value = live?.value ?? rhfField.value ?? "";
              return (
                <FormItem>
                  <FormLabel>Disposal Avenue</FormLabel>
                  <LiveField lockedBy={live?.lockedBy ?? null}>
                    <FormControl>
                      <Input
                        value={value}
                        onChange={(e) => { rhfField.onChange(e.target.value); live?.onChange(e.target.value); }}
                        onFocus={live?.onFocus}
                        onBlur={() => { rhfField.onBlur(); live?.onBlur(); }}
                        disabled={live ? !live.isEditable : false}
                        placeholder="e.g., Auction, Donation Center"
                      />
                    </FormControl>
                  </LiveField>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>

        {/* Financials */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Financials</h3>

          <FormField
            control={form.control}
            name="asking_price"
            render={({ field: rhfField }) => {
              const live = liveField?.<number | null>("asking_price");
              const value = live?.value ?? rhfField.value ?? null;
              return (
                <FormItem>
                  <FormLabel>
                    Asking Price
                    {lifecycleStatus && ['Valued', 'Listed for Sale', 'Under Offer', 'Sold'].includes(lifecycleStatus) && ' *'}
                  </FormLabel>
                  <LiveField lockedBy={live?.lockedBy ?? null}>
                    <FormControl>
                      <CurrencyInput
                        value={value}
                        onChange={(val) => { rhfField.onChange(val); live?.onChange(val); }}
                        placeholder="0.00"
                        disabled={live ? !live.isEditable : false}
                      />
                    </FormControl>
                  </LiveField>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="lowest_acceptable_price"
            render={({ field: rhfField }) => {
              const live = liveField?.<number | null>("lowest_acceptable_price");
              const value = live?.value ?? rhfField.value ?? null;
              return (
                <FormItem>
                  <FormLabel>Lowest Acceptable Price</FormLabel>
                  <LiveField lockedBy={live?.lockedBy ?? null}>
                    <FormControl>
                      <CurrencyInput
                        value={value}
                        onChange={(val) => { rhfField.onChange(val); live?.onChange(val); }}
                        placeholder="0.00"
                        disabled={live ? !live.isEditable : false}
                      />
                    </FormControl>
                  </LiveField>
                  <FormDescription>
                    Must be less than or equal to asking price
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="current_highest_offer"
            render={({ field: rhfField }) => {
              const live = liveField?.<number | null>("current_highest_offer");
              const value = live?.value ?? rhfField.value ?? null;
              return (
                <FormItem>
                  <FormLabel>Current Highest Offer</FormLabel>
                  <LiveField lockedBy={live?.lockedBy ?? null}>
                    <FormControl>
                      <CurrencyInput
                        value={value}
                        onChange={(val) => { rhfField.onChange(val); live?.onChange(val); }}
                        placeholder="0.00"
                        disabled={live ? !live.isEditable : false}
                      />
                    </FormControl>
                  </LiveField>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="final_sale_price"
            render={({ field: rhfField }) => {
              const live = liveField?.<number | null>("final_sale_price");
              const value = live?.value ?? rhfField.value ?? null;
              return (
                <FormItem>
                  <FormLabel>Final Sale Price</FormLabel>
                  <LiveField lockedBy={live?.lockedBy ?? null}>
                    <FormControl>
                      <CurrencyInput
                        value={value}
                        onChange={(val) => { rhfField.onChange(val); live?.onChange(val); }}
                        placeholder="0.00"
                        disabled={live ? !live.isEditable : false}
                      />
                    </FormControl>
                  </LiveField>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="shipping_costs"
            render={({ field: rhfField }) => {
              const live = liveField?.<number | null>("shipping_costs");
              const value = live?.value ?? rhfField.value ?? null;
              return (
                <FormItem>
                  <FormLabel>Shipping Costs</FormLabel>
                  <LiveField lockedBy={live?.lockedBy ?? null}>
                    <FormControl>
                      <CurrencyInput
                        value={value}
                        onChange={(val) => { rhfField.onChange(val); live?.onChange(val); }}
                        placeholder="0.00"
                        disabled={live ? !live.isEditable : false}
                      />
                    </FormControl>
                  </LiveField>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>

        {/* Delivery */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Delivery</h3>

          <FormField
            control={form.control}
            name="delivery_method"
            render={({ field: rhfField }) => {
              const live = liveField?.("delivery_method");
              const value = live?.value ?? rhfField.value ?? "none";
              return (
                <FormItem>
                  <FormLabel>Delivery Method</FormLabel>
                  <LiveField lockedBy={live?.lockedBy ?? null}>
                    <Select
                      value={String(value || "none")}
                      onValueChange={(v) => {
                        const val = v === "none" ? null : v;
                        rhfField.onChange(val);
                        live?.onChange(val as any);
                      }}
                      disabled={live ? !live.isEditable : false}
                    >
                      <FormControl>
                        <SelectTrigger onFocus={live?.onFocus} onBlur={live?.onBlur}>
                          <SelectValue placeholder="Select method" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {deliveryMethods.map((method) => (
                          <SelectItem key={method} value={method}>
                            {method}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </LiveField>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {deliveryMethod === 'Other' && (
            <FormField
              control={form.control}
              name="delivery_method_other"
              render={({ field: rhfField }) => {
                const live = liveField?.("delivery_method_other");
                const value = live?.value ?? rhfField.value ?? "";
                return (
                  <FormItem>
                    <FormLabel>Other Delivery Method</FormLabel>
                    <LiveField lockedBy={live?.lockedBy ?? null}>
                      <FormControl>
                        <Input
                          value={value}
                          onChange={(e) => { rhfField.onChange(e.target.value); live?.onChange(e.target.value); }}
                          onFocus={live?.onFocus}
                          onBlur={() => { rhfField.onBlur(); live?.onBlur(); }}
                          disabled={live ? !live.isEditable : false}
                        />
                      </FormControl>
                    </LiveField>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          )}
        </div>

        {/* Marketing */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Marketing</h3>

          <FormField
            control={form.control}
            name="marketing_description"
            render={({ field: rhfField }) => {
              const live = liveField?.("marketing_description");
              const value = live?.value ?? rhfField.value ?? "";
              return (
                <FormItem>
                  <FormLabel>Marketing Description</FormLabel>
                  <LiveField lockedBy={live?.lockedBy ?? null}>
                    <FormControl>
                      <RichTextEditor
                        content={value}
                        onChange={(val) => { rhfField.onChange(val); live?.onChange(val); }}
                        placeholder="Describe this asset for potential buyers..."
                        editable={live ? live.isEditable : true}
                      />
                    </FormControl>
                  </LiveField>
                  <FormDescription>
                    Use this to create compelling descriptions for listings
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>

        {/* Workflow */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Workflow</h3>

          <FormField
            control={form.control}
            name="share_with_external"
            render={({ field: rhfField }) => {
              const live = liveField?.<boolean>("share_with_external");
              const value = live?.value ?? rhfField.value ?? false;
              return (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <LiveField lockedBy={live?.lockedBy ?? null}>
                    <FormControl>
                      <Checkbox
                        checked={value}
                        onCheckedChange={(checked) => { rhfField.onChange(checked); live?.onChange(checked as boolean); }}
                        disabled={live ? !live.isEditable : false}
                      />
                    </FormControl>
                  </LiveField>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      Share with External Users
                    </FormLabel>
                    <FormDescription>
                      Allow external users to view this asset
                    </FormDescription>
                  </div>
                </FormItem>
              );
            }}
          />
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Saving...' : mode === 'create' ? 'Create Asset' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
